import React, { FC, PropsWithChildren, ReactNode, useRef, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import throttle from "lodash/throttle";

import "./Tooltip.scss";

export interface StaticTooltipProps extends PropsWithChildren {
  content: ReactNode;
  position?: "top" | "top-end" | "bottom" | "bottom-end" | "left" | "bottom-right";
}

export const StaticTooltip: FC<StaticTooltipProps> = ({ content, children, position = "bottom" }) => {
  const [tooltipPortal, setTooltipPortal] = useState<React.ReactNode>();
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = (evt: React.MouseEvent) => {
    const { x, y, height, width } = evt.currentTarget.getBoundingClientRect();

    let top, left;
    let transform = "";

    switch (position) {
      case "top":
        top = y - height - 10 + window.scrollY - (tooltipRef.current ? tooltipRef.current.offsetHeight / 2 : 0);
        left = x + width / 2;
        transform = "translateX(-50%)";
        break;
      case "top-end":
        top = y - height * 2 - 20 - (tooltipRef.current ? tooltipRef.current.offsetHeight / 2 : 0);
        left = x + width;
        transform = "translateX(-100%)";
        break;
      case "bottom":
        top = y + height + 10 + window.scrollY;
        left = x + width / 2;
        transform = "translateX(-50%)";
        break;
      case "bottom-end":
        top = y + height + 10 + window.scrollY;
        left = x + width;
        transform = "translateX(-100%)";
        break;
      case "bottom-right":
        top = y + height + 10 + window.scrollY;
        left = x;
        transform = "";
        break;
      case "left":
        top = y - 10 + window.scrollY;
        left = x - 10;
        transform = "translateX(-100%)";
        break;
    }

    const style = { top: `${top}px`, left: `${left}px`, transform };

    if (content !== null) {
      setTooltipPortal(
        <div className="tooltip-content" style={style}>
          {content}
        </div>,
      );
    }
  };

  const handleMouseLeave = () => {
    setTooltipPortal(null);
  };

  useEffect(() => {
    const handleMouseMove = throttle((evt: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(evt.target as Node)) {
        handleMouseLeave();
      }
    }, 100);

    document.addEventListener("mousemove", handleMouseMove);

    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <>
      <div className="tooltip" ref={tooltipRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
      </div>
      {tooltipPortal && ReactDOM.createPortal(tooltipPortal, document.body)}
    </>
  );
};
